/**
 * Block Helpers.
 * 
 * Handles a lot of utility work surrounding blocks and other goodies.
 */

/** Dependencies -------------------------------------------- */

// Wordpress

import { Children, cloneElement, Fragment  } from '@wordpress/element'

// Design system

import {
    isObject,
    filterObject
} from '../helpers'
import {
    atomicSpacing,
    breakpoints,
    colors,
    colorContexts,
	defaultBreakpoint
} from './style-guide'

/** Components ---------------------------------------------- */

/**
 * A component that simply clones its children and passes its props to it.
 */
export function Heirs( props  ) {

    const { children, ...additionalProps } = props
    const Heirs = () => Children.map( children, ( child ) => cloneElement( child, additionalProps ) )

    return (
        <Fragment>
            { Heirs() }
        </Fragment>
    )
}

/** Functions ----------------------------------------------- */

/**
 * A simple noop function.
 */
export const noop = () => {}

/**
 * Gets responsive classNames from a responsive prop.
 */
export function getResponsiveClassNames( prefix, prop, ...propKeys ) {

    const responsiveClassNames = []

    for ( const [ breakpoint, valueAtBreakpoint ] of Object.entries( filterObject( prop, ( value ) => value !== '' ) ) ) {

        let resolvedValue = valueAtBreakpoint

        if ( typeof resolvedValue === 'object' ) {
            for ( const propKey of propKeys ) {
                resolvedValue = resolvedValue[propKey]
            }
        }

        if ( resolvedValue !== '' ) {
            responsiveClassNames.push( `${prefix}-${resolvedValue}@${breakpoint}` )
        }
    }

    return responsiveClassNames

    // This was the working way before
    // return Object.entries( filterObject( prop ) ).map(
    //     ( [ breakpoint, valueAtBreakpoint ] ) => `${prefix}-${valueAtBreakpoint}@${breakpoint}`
    // )

}

/**
 * Gets a block's DOM node via its client ID.
 */
export function getBlockDOMNode( clientId ) {
    return document.querySelector( `[data-block='${clientId}']` )
}

/**
 * Gets a color context from a given hex value.
 */
export function getColorContextFromHex( hex, additionalContexts = [] ) {
    return colorContexts.concat( additionalContexts ).find(
        ( colorContext ) => hex === colors[colorContext.color].hex
    )
}

/**
 * Gets a color context from a given value.
 */
export function getColorContextByValue( value, additionalContexts = [] ) {
    return colorContexts.concat( additionalContexts ).find(
        ( colorContext ) => colorContext.value === value
    )
}

/**
 * Normalizes a link-like prop to better adhere to HTML instead of wordpress silliness.
 */
export function normalizeLinkProp( prop ) {

    if ( typeof prop === 'string' ) {
        prop = { url: prop }
    }

    const rels = [
        'noopener',
        ...( prop.opensInNewTab ? [ 'noreferrer' ] : [] )
    ]

    const label = prop.label || `Go to ${prop.url}`

    return {
        ...prop,
        href   : prop.url || '',
        target : prop.opensInNewTab ? '_blank' : '_self',
        rel    : rels.join( ' ' ),
        label  : label
    }
}

/**
 * Normalizes an image-like prop to better adhere to HTML instead of wordpress silliness.
 */
export function normalizeImageProp( prop ) {

    let url = prop.url || prop.source_url
    const sizes = prop.sizes || prop.media_details?.sizes || {}

    const size = prop.wpSize
    const propAtSize = sizes[size]

    if ( size && propAtSize ) {
        url = propAtSize.url || propAtSize.source_url || url
    }

    return {
        ...prop,
        alt : prop?.alt || prop?.alt_text || '',
        src : url
    }
}

/**
 * Normalizes a date prop so that we don't magically get transported back to 1970..
 */
export function normalizeDateProp( prop ) {
    return ( prop ? new Date( prop ) : new Date() )
}

/**
 * Gets a block's currently applied style from its className.
 */
export function getBlockCurrentStyle( className ) {

    const matches = className.match( /(?<=is-style-)(.*)(?=[\s\b])/ ) || []
    return matches[0] || null
}

/**
 * Converts an RGB string to hex.
 */
export function rgbaToHex( rgba ) {

    const hexValues = rgba
        .match( /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+\.{0,1}\d*))?\)$/ )
        .slice( 1 )
        .map(
            ( n, i ) => {
                return ( i === 3 ? Math.round( parseFloat( n ) * 255 ) : parseFloat( n ) )
                    .toString( 16 )
                    .padStart( 2, '0' )
                    .replace( 'NaN', '' )
            }
        )

    return `#${hexValues.join( '' )}`
}

/**
 * Derives a background color context from an element given its actual background color.
 */
export function deriveBackgroundColorContext( element ) {

    const backgroundColor = window.getComputedStyle( element ).getPropertyValue( 'background-color' )
    const colorContext = getColorContextFromHex( rgbaToHex( backgroundColor ) )

    return colorContext
}

/**
 * Decodes an HTML string into normal characters.
 * 
 * @link https://stackoverflow.com/questions/1912501/unescape-html-entities-in-javascript
 */
export function decodeHtml( string ) {
    var dom = new DOMParser().parseFromString( string, 'text/html' );
    return dom.documentElement.textContent;
}

/**
 * Consolidates a responsive value from given ones and returns it.
 * Essentially fills in "missing" breakpoints where the cascade implies it.
 */
export function getConsolidatedResponsiveValue( value, breakpoint = null ) {

	value = value || { [defaultBreakpoint]: '' }
    const breakpointKeys = Object.keys( breakpoints )

    let consolidatedValue = {}

    for ( const breakpointKey of breakpointKeys ) {

        if ( breakpointKey in value && value[breakpointKey] !== '' ) {

            const valueAtBreakpoint = value[breakpointKey]

            if ( isObject( consolidatedValue ) && isObject( valueAtBreakpoint ) ) {
                consolidatedValue = { ...consolidatedValue, ...filterObject( valueAtBreakpoint, ( value ) => value !== '' ) }
            }
            else {
                consolidatedValue = valueAtBreakpoint
            }
        }
        if ( breakpointKey === breakpoint ) {
            break
        }
    }

    return consolidatedValue
}

/**
 * Gets a single spacing control value's CSS value.
 */
export function getSpacingCssValue( controlValue, defaultValue = '' ) {

    const { type, value } = controlValue || {}
    let cssValue = ''

    switch ( type ) {

        case 'auto':
            cssValue = 'auto'
            break

        case 'named':
            cssValue = `var( --${value}Spacing )`
            break
        
        case 'atomic':
            cssValue = `${atomicSpacing * value}px`
            break

        case 'unit':
        case 'custom':
            cssValue = value
            break

        default:
            cssValue = defaultValue
    }

    return cssValue
}

